/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {GridRenderCellParams} from '@mui/x-data-grid';
import BeamTooltip from '../beam/BeamTooltip';
import {TooltipProps} from "@mui/material";
import styled from 'styled-components';

const Root = styled.div`
  align-items: center;
  line-height: 24px;
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
`;

const Cell = styled.div`
  height: 1px;
  position: absolute;
  display: block;
  top: 0;
`

const CellValue = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface TooltipCellProps {
  width: number;
  value: string;
}

/**
 * TooltipCell component is used to render a cell with a tooltip in a data grid.
 * It displays the full content of the cell in a tooltip when the cell content is overflown.
 *
 * @ref https://v5.mui.com/x/react-data-grid/column-definition/#RenderExpandCellGrid.tsx
 * @component
 * @param {TooltipCellProps & Pick<TooltipProps, 'placement'>} props The properties for the TooltipCell component.
 * @param {number} props.width The width of the cell.
 * @param {string} props.value The value to be displayed in the cell.
 * @param {TooltipProps['placement']} props.placement The placement of the tooltip.
 * @returns {JSX.Element} The rendered TooltipCell component.
 */
const TooltipCell = (props: TooltipCellProps & Pick<TooltipProps, 'placement'>): JSX.Element => {
  const { width, value, placement } = props;
  const wrapper = useRef<HTMLDivElement>(null);
  const cellDiv = useRef<HTMLDivElement>(null);
  const cellValue = useRef<HTMLDivElement>(null);
  const [showFullCell, setShowFullCell] = useState(false);

  /**
   * Checks if the given element is overflown.
   *
   * @param {HTMLDivElement | null} element The element to check.
   * @returns {boolean} True if the element is overflown, false otherwise.
   */
  const isOverflown = (element: HTMLDivElement | null): boolean => {
    if (!element) {
      return false;
    }
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  };

  const handleMouseEnter = () => {
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  // Handle the 'Esc' key to close the tooltip
  useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    const handleKeyDown = (nativeEvent: KeyboardEvent) => {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  const isCurrentlyOverflown = isOverflown(cellValue.current);
  return (
    <Root ref={wrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Cell
        ref={cellDiv}
        style={{
          width,
        }}
      />
      {isCurrentlyOverflown ? (
        <BeamTooltip title={value} placement={placement} open={showFullCell}>
          <CellValue ref={cellValue} className="cellValue">
            {value}
          </CellValue>
        </BeamTooltip>
      ) : (
        <CellValue ref={cellValue}>{value}</CellValue>
      )}
    </Root>
  );
};

/**
 * RenderTooltipCell is a higher-order function that returns a function to render a {@link TooltipCell} component. It should
 * be used as the `renderCell` property of a column definition in a data grid.
 *
 * The {@link TooltipCell} component is a styled tooltip that displays the full text of a cell value when the cell is
 * hovered over. If the cell value is not overflowing, the full text is displayed in the cell itself.
 *
 * @param {TooltipProps['placement']} placement The placement of the tooltip.
 * @returns {function(GridRenderCellParams): JSX.Element} A function that renders a TooltipCell component.
 */
const RenderTooltipCell = (placement: TooltipProps['placement']): ((params: GridRenderCellParams) => JSX.Element) => {
  return (params: GridRenderCellParams) => {
    return (
      <TooltipCell
        placement={placement}
        value={params.value ? params.value.toString() : ''}
        width={params.colDef.computedWidth}
      />
    );
  };
};

export { RenderTooltipCell };
